import z from "zod/v3";
import common from "../utils/common";

export const nonEmptySessionString = z
  .string()
  .refine((s) => !common.stringIsNullOrEmpty(s), { message: "required" });

export const getDefaultSessionValue = () => {
  const session = {
    id: "",
    username: "",
    role: "",
    isAdmin: false,
  };
  return common.removeUndefinedProperty(session);
};

export const userSessionToCookie = (session: UserSession) => {
  return {
    id: session.id,
    username: session.username,
    role: session.role,
    isAdmin: session.isAdmin,
  };
};

/** Plain session payload (e.g. cookies); matches {@link UserSession} fields. */
export const userSessionSchema = z.object({
  id: nonEmptySessionString,
  username: nonEmptySessionString,
  role: nonEmptySessionString,
  isAdmin: z.boolean().optional(),
  token: z.string().optional(),
});

export type UserSessionData = z.infer<typeof userSessionSchema>;

export const loginRequestSchema = z.object({
  username: z.string(),
  password: z.string(),
});

export const loginServiceResultSchema = z.object({
  id: z.number().int(),
  username: z.string(),
  token: z.string(),
});

export const searchUserRequestSchema = z.object({
  id: z.array(z.number().int()).optional(),
  keyword: z.string().optional(),
  username: z.array(z.string()).optional(),
  create_date: z.array(z.coerce.date()).optional(),
  is_deleted: z.boolean().optional(),
});

export const createUserRequestSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
  role: z.enum(["USER", "ADMIN"]).optional(),
});

export const updateUserRequestSchema = z.object({
  username: z.string().trim().min(1),
  role: z.enum(["USER", "ADMIN"]),
  password: z.string().min(1).optional(),
});

export const changePasswordRequestSchema = z.object({
  password: z.string(),
  param: z.string().optional(),
});

export const resetPasswordEmailRequestSchema = z.object({
  email: z.string(),
  expires: z.number(),
});

export const resetPasswordRequestSchema = z.object({
  email: z.string(),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type LoginServiceResult = z.infer<typeof loginServiceResultSchema>;
export type SearchUserRequest = z.infer<typeof searchUserRequestSchema>;
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;
export type UserSession = z.infer<typeof userSessionSchema>;
export type ResetPasswordEmailRequest = z.infer<
  typeof resetPasswordEmailRequestSchema
>;
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;
